import type { CashbookExportEmailFormat, CashbookViewMode } from '@finvox/shared/cashbook';
import { apiPost } from '@/lib/api';
import {
  buildCashbookCsvBlob,
  buildCashbookPdfBlob,
  buildCashbookXlsxBlob,
  type CashbookExportInput
} from '@/lib/cashbookExport';

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

async function buildExportBlob(
  format: CashbookExportEmailFormat,
  input: CashbookExportInput
): Promise<Blob> {
  if (format === 'csv') return buildCashbookCsvBlob(input);
  if (format === 'xlsx') return buildCashbookXlsxBlob(input);
  return buildCashbookPdfBlob(input);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

export async function emailCashbookExportToOwner(opts: {
  orgId: string;
  format: CashbookExportEmailFormat;
  input: CashbookExportInput;
  viewMode: CashbookViewMode;
}): Promise<{ sent: boolean; reason?: string }> {
  const ext = opts.format === 'xlsx' ? 'xlsx' : opts.format;
  const filename = `${opts.input.filenameBase}.${ext}`;
  const blob = await buildExportBlob(opts.format, opts.input);

  if (blob.size < 1) return { sent: false, reason: 'empty_file' };
  if (blob.size > MAX_ATTACHMENT_BYTES) return { sent: false, reason: 'file_too_large' };

  try {
    const result = await apiPost<{ sent: boolean; reason?: string }>('/v1/cashbook/export-email', {
      orgId: opts.orgId,
      format: opts.format,
      filename,
      contentBase64: await blobToBase64(blob),
      periodLabel: opts.input.header.periodLabel,
      viewMode: opts.viewMode
    });
    return result;
  } catch (e) {
    return { sent: false, reason: e instanceof Error ? e.message : 'network_error' };
  }
}
